import { useEffect, useState } from "react";
import { listAnalyses, listDraftProfiles, listProfileOverrides } from "../../db";
import { listProfiles } from "../../profiles";
import { useProfileOverrideStore } from "../../stores/profile-override-store";
import { buildFleetRows, summarizeFleet, type FleetRow } from "./fleet";

export type FleetState = {
  rows: FleetRow[];
  counts: ReturnType<typeof summarizeFleet>;
  /** True until the first read settles. */
  loading: boolean;
};

/**
 * The fleet, read once per override change.
 *
 * The three reads are independent and each already degrades to an empty list,
 * so a failing IndexedDB yields the committed profiles with no run history
 * rather than an error page — the registry is a module constant and is always
 * available. Re-runs on `overridesRevision` so saving or resetting an override
 * in the detail pane is reflected in the list beside it.
 */
export function useFleet(): FleetState {
  const overridesRevision = useProfileOverrideStore((state) => state.revision);
  const [rows, setRows] = useState<FleetRow[]>(() => buildFleetRows({ profiles: listProfiles() }));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listDraftProfiles(), listProfileOverrides(), listAnalyses()]).then(
      ([drafts, overrides, analyses]) => {
        if (cancelled) return;
        setRows(buildFleetRows({ profiles: listProfiles(), drafts, overrides, analyses }));
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
    // `overridesRevision` is a trigger, not a value read here: saving or
    // resetting an override bumps it so this list re-reads.
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [overridesRevision]);

  return { rows, counts: summarizeFleet(rows), loading };
}
